import { CoralogixRum } from '@coralogix/browser'
import { Component, type ErrorInfo, type ReactNode } from 'react'

import { useReportErrorsCallback } from '@/lib/connect/error-reporting'

import { Empty } from '@/components/lemmala-design-system/empty/empty'

import { ERROR_BOUNDARY_LABELS } from './labels'
import { isChunkLoadError, reloadForStaleChunk } from './utils'

interface ErrorBoundaryProps {
  children: ReactNode
  fallback?: ReactNode
  onError?: (error: Error, componentStack?: string) => void
}

interface ErrorBoundaryState {
  hasError: boolean
  sessionId?: string
}

class ErrorBoundaryInner extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  state: ErrorBoundaryState = { hasError: false }

  static getDerivedStateFromError(): ErrorBoundaryState {
    return { hasError: true, sessionId: CoralogixRum.getSessionId() }
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    this.props.onError?.(error, info.componentStack ?? undefined)
    if (this.props.fallback === undefined && isChunkLoadError(error)) reloadForStaleChunk()
  }

  render() {
    if (this.state.hasError) {
      if (this.props.fallback !== undefined) {
        return this.props.fallback
      }

      return (
        <div className='flex h-screen items-center justify-center'>
          <Empty
            icon='not-found'
            title={ERROR_BOUNDARY_LABELS.title}
            subtitle={
              <>
                {ERROR_BOUNDARY_LABELS.subtitle}
                {this.state.sessionId && (
                  <span className='mt-2 block text-main-foreground-tertiary text-body-small'>
                    {ERROR_BOUNDARY_LABELS.sessionId(this.state.sessionId)}
                  </span>
                )}
              </>
            }
            button={{
              label: ERROR_BOUNDARY_LABELS.tryAgainButton,
              onClick: () => window.location.reload(),
            }}
          />
        </div>
      )
    }

    return this.props.children
  }
}

export const ErrorBoundary = ({ children, fallback }: Omit<ErrorBoundaryProps, 'onError'>) => {
  const reportErrors = useReportErrorsCallback()

  return (
    <ErrorBoundaryInner fallback={fallback} onError={(error) => reportErrors(error)}>
      {children}
    </ErrorBoundaryInner>
  )
}
